import { Injectable } from '@angular/core';
import { EditorSettings } from '../liveformseditor/types/editor-settings';
import { SupportedLanguages } from '../shared/supported-languages';

// Import translation files
import * as enTranslations from '../i18n/en.json';
import * as deTranslations from '../i18n/de.json';

@Injectable({
  providedIn: 'root'
})
export class LiveFormsTranslateLoaderService {
  private editorSettings: EditorSettings = new EditorSettings();

  private translations: { [key: string]: any } = {
    en: enTranslations,
    de: deTranslations
  };

  /**
   * Set the editor settings to get language from
   */
  setEditorSettings(settings: EditorSettings): void {
    this.editorSettings = settings;
  }

  /**
   * Get the current language from editor settings.
   */
  getCurrentLanguage(): SupportedLanguages {
    return this.editorSettings.language;
  }

  /**
   * Get a nested value from an object using a dot-notation key
   */
  private getNestedValue(obj: any, key: string): string | null {
    return key.split('.').reduce((current, keyPart) => {
      return current && current[keyPart] !== undefined ? current[keyPart] : null;
    }, obj);
  }

  /**
   * Get instant translation with fallback
   */
  instant(key: string, params?: Record<string, string | number>): string {
    const currentLang = this.getCurrentLanguage();
    const currentTranslations = this.translations[currentLang];
    const fallbackTranslations = this.translations['en'];

    // Try to get translation from current language
    let translation = this.getNestedValue(currentTranslations, key);

    // If not found and current language is not English, try English fallback
    if (translation === null && currentLang !== 'en') {
      translation = this.getNestedValue(fallbackTranslations, key);
    }

    // If still not found, return the key itself
    if (translation === null) {
      console.warn(`LiveForms Editor - Translation key not found: ${key}`);
      return key;
    }

    // Handle parameter interpolation if params are provided
    if (params && translation) {
      return this.interpolateParams(translation, params);
    }

    return translation;
  }

  /**
   * Interpolate parameters in translation string
   */
  private interpolateParams(translation: string, params: any): string {
    return translation.replace(/{{(\w+)}}/g, (match, key) => {
      return params[key] !== undefined ? params[key] : match;
    });
  }
}
